import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * 대장 엑셀이 <b>저장됐다</b>는 소식을 탭에서 탭으로 나른다 (사용자 지시 2026-09-23).
 *
 * <h2>왜 필요한가 — 고치는 자리와 보는 자리가 다른 탭이다</h2>
 * 「엑셀로 열기」는 새 탭을 연다. 거기서 고친 값은 문서서버 → 저장 통보 → {@code LedgerWorkbookService.onSaved}
 * 를 지나 대장(DB)까지 들어가는데, <b>그 사실을 원래 탭이 알 길이 없었다</b>. 사람이 탭을 되돌려도
 * 표는 열 때 받아 둔 값을 그대로 그린다({@code staleTime} 30초) — 「엑셀에 넣었는데 화면에 없다」.
 *
 * <h2>두 번 다시 읽는 이유</h2>
 * 편집 탭이 아는 시각은 <b>「지금 저장하라」고 지시한 순간</b>이지 대장에 들어간 순간이 아니다.
 * 그 뒤로 문서서버가 파일을 조립하고 → 콜백을 보내고 → 백엔드가 받아 S3 에 쓰고 → 줄을 읽어 대장에
 * 반영한다. 소식을 받자마자 한 번만 읽으면 <b>아직 옛 값</b>이 온다. 그래서 잠깐 뒤·조금 더 뒤로
 * 두 번 읽는다 — 큰 파일에서 늦는 쪽까지 덮는다.
 *
 * <h2>무엇을 다시 읽나 — 전부</h2>
 * 대장은 13곳의 화면이 쓰고, 어느 대장이 어느 질의에 닿는지는 화면마다 다르다. 그 지도를 여기에
 * 또 한 벌 두면 새 대장이 생길 때마다 <b>이 파일을 고치는 것을 잊는</b> 쪽으로 틀린다 — 그때 증상은
 * 지금과 똑같다. 대신 캐시를 통째로 무효화한다: 다시 읽는 것은 <b>지금 떠 있는 질의뿐</b>이고,
 * 대장 저장은 몇 분에 한 번 있는 일이다.
 */
const CHANNEL = 'bomi-ledger-saved';

/** 소식을 받고 다시 읽는 시점(ms). 앞은 평소, 뒤는 파일이 커서 늦는 경우. */
const RELOAD_DELAYS = [1_500, 5_000];

/**
 * <b>편집기를 띄우고 있는 탭에서는 다시 읽지 않는다.</b>
 *
 * <p>소식을 보낸 탭 자신도 이 채널을 듣는다(브라우저는 «같은 탭의 다른 채널 객체» 에는 배달한다).
 * 그대로 두면 캐시 무효화가 편집기 설정({@code office-config})까지 다시 읽고, 새로 온 설정 객체가
 * 편집기 effect 의 의존값이라 <b>고치고 있던 편집기가 통째로 다시 선다</b> — 옆 탭에서 다른 대장을
 * 저장했을 때도 마찬가지다. 그 탭에는 새로 그릴 표도 없다.
 */
const EDITOR_PATH = '/docs/office';

function editing(): boolean {
  return window.location.pathname.startsWith(EDITOR_PATH);
}

export type LedgerSaved = {
  /** 어느 대장인가. 로그에만 쓴다 — 다시 읽는 범위는 이 값으로 좁히지 않는다(머리말). */
  ledgerKey: string;
  s3Key: string;
  at: number;
};

function open(): BroadcastChannel | null {
  // 오래된 웹뷰에는 없다. 없으면 이 기능만 조용히 빠진다 — 저장 자체는 그대로 된다.
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL);
  }
  catch {
    return null;
  }
}

/** 편집 탭이 부른다. 보내는 쪽은 자기 소식을 받지 않는다(BroadcastChannel 규칙). */
export function publishLedgerSaved(ledgerKey: string, s3Key: string): void {
  const channel = open();
  if (!channel) return;
  try {
    channel.postMessage({ ledgerKey, s3Key, at: Date.now() } satisfies LedgerSaved);
  }
  finally {
    channel.close();
  }
}

/**
 * 화면 탭이 듣는다. {@code AppLayout} 에 한 번만 단다 — 화면마다 달면 탭을 옮길 때마다 붙었다 떨어진다.
 */
export function useLedgerSaveListener(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = open();
    if (!channel) return;

    const timers: number[] = [];
    channel.onmessage = () => {
      if (editing()) return;
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.length = 0;
      RELOAD_DELAYS.forEach((delay) => {
        // 기다리는 사이에 이 탭이 편집기로 갔을 수 있다. 그때는 그만둔다.
        timers.push(window.setTimeout(() => { if (!editing()) queryClient.invalidateQueries(); }, delay));
      });
    };

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      channel.close();
    };
  }, [queryClient]);
}
